import { useState, useEffect, useCallback, createContext } from "react";
import { createPortal } from "react-dom";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import AlertWidget from "./features/monitoring/components/AlertWidget";
import { useAlertWidget } from "./features/monitoring/hooks/useAlertWidget";
import LoginPage from "./features/auth/pages/LoginPage";
import EmailRegisterPage from "./features/auth/pages/EmailRegisterPage";
import EmailResetPasswordPage from "./features/auth/pages/EmailResetPasswordPage";
import ProtectedRoute from "./ProtectedRoute";
import TasksViewV2Page from "./features/tasks/pages/TasksViewV2Page";
import DashboardViewV2Page from "./features/dashboard/pages/DashboardViewV2Page";
import EpicsViewV2Page from "./features/epics/pages/EpicsViewV2Page";
import DependenciesViewV2Page from "./features/dependencies/pages/DependenciesViewV2Page";
import UsersViewV2Page from "./features/users/pages/UsersViewV2Page";
import FeaturesViewV2Page from "./features/features/pages/FeaturesViewV2Page";
import TimesheetViewV2Page from "./features/timesheet/pages/TimesheetViewV2Page";
import MeetingsViewV2 from "./MeetingsViewV2";
import DefectsViewV2Page from "./features/defects/pages/DefectsViewV2Page";
import RetroViewV2Page from "./features/retro/pages/RetroViewV2Page";
import PresalesViewV2Page from "./features/presales/pages/PresalesViewV2Page";
import CopilotPage from "./features/copilot/pages/CopilotPage";
import ProjectsViewV2Page from "./features/projects/pages/ProjectsViewV2Page";
import ReportsViewV2Page from "./features/reports/pages/ReportsViewV2Page";
import RbacManagementPage from "./features/rbac/pages/RbacManagementPage";
import SettingsPage from "./features/settings/pages/SettingsPage";
import TodoView from "./TodoView";
import ProductReleasesView from "./ProductReleasesView";
import { PermissionsProvider } from "./context/PermissionsContext";
import { API, clearCache } from "./cache";
import "./App.css";

const TEAMS_RELOGIN_RESUME_KEY = "protrack_teams_relogin_resume";
const TEAMS_RELOGIN_RESUME_MAX_AGE_MS = 30 * 60 * 1000;

function isAdminInternal(user) {
  const role = String(user?.db_role || "").trim().toLowerCase();
  return (role === "admin" || role === "super admin") && user?.db_user_type === "Internal";
}

function isSuperAdminInternal(user) {
  return user?.db_role === "Super Admin" && user?.db_user_type === "Internal";
}

function isAdminOrAmInternal(user) {
  const role = String(user?.db_role || "").trim().toLowerCase();
  const userType = String(user?.db_user_type || "").trim().toLowerCase();
  return (role === "admin" || role === "super admin" || role === "am") && (userType === "internal" || userType === "");
}

// Gate for the /reports route: Admin/Super Admin (any customer/project),
// AM, or a Team Lead (user?.is_team_lead === true). AM/Team-Lead callers
// only see "Efforts by Customer"/"Epic Effort Report" once inside — see
// ReportsView.jsx's own catalog filtering — every other report stays
// Admin-only, gated purely by the backend's own require_admin dependency.
function isAdminAmOrTeamLead(user) {
  return isAdminOrAmInternal(user) || user?.is_team_lead === true;
}

function hasSiteAccess(user) {
  return !!String(user?.db_role || "").trim();
}

function isExternalUser(user) {
  const t = String(user?.db_user_type || "").toLowerCase();
  return t === "external" || t === "external user";
}

// Old/duplicate paths. They still appear in posted Teams notifications
// (teams_notifier.py used /masterv2new, /defectsv2new, /meetingsv2new) and
// bookmarks, so they redirect -- query string and hash preserved -- to the
// one canonical route the sidebar highlights, rather than rendering a copy.
const ROUTE_ALIASES = {
  "/master":         "/masterv2",
  "/masterv2new":    "/masterv2",
  "/defects":        "/defectsv2",
  "/defectv2new":    "/defectsv2",
  "/defectsv2new":   "/defectsv2",
  "/meetings":       "/meetingsv2",
  "/meetingsv2new":  "/meetingsv2",
  "/retro":          "/retrov2new",
};

const PUBLIC_AUTH_PATHS = new Set(["/email-signin", "/email-register", "/email-reset-password"]);

function AliasRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function NoAccessPage({ onLogout }) {
  return (
    <div className="app-noaccess">
      <div className="app-noaccess__card">
        <div className="app-noaccess__eyebrow">Access</div>
        <h1 className="app-noaccess__title">You don't have access to ProTrack</h1>
        <p className="app-noaccess__text">Your account isn't assigned a role yet. Ask a ProTrack administrator to grant access.</p>
        <button type="button" className="app-noaccess__btn" onClick={onLogout}>Sign out</button>
      </div>
    </div>
  );
}

const EXTERNAL_ALLOWED_PATHS = new Set([
  "/masterv2",
  "/masterv2new",
  "/dependencies",
  "/defectsv2",
  "/defectsv2new",
  "/releases",
]);

export const AlertWidgetContext = createContext(null);

const MON_REFRESH_LS_KEY = "mon_refresh_interval";

export default function App() {
  const [toast, setToast]   = useState(null);
  const [user, setUser]     = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  // ── Alert widget — lives at App level so it survives navigation ──────────────
  const [monRefreshInterval, setMonRefreshInterval] = useState(() => {
    const stored = localStorage.getItem(MON_REFRESH_LS_KEY);
    return stored !== null ? Number(stored) : 0;
  });
  const handleMonRefreshChange = useCallback((val) => {
    setMonRefreshInterval(val);
    localStorage.setItem(MON_REFRESH_LS_KEY, String(val));
  }, []);
  const {
    isOpen:          widgetOpen,
    pipWindow:       widgetPipWindow,
    launch:          launchWidget,
    close:           closeWidget,
    firingCustomers: widgetFiringCustomers,
    lastUpdated:     widgetLastUpdated,
  } = useAlertWidget(monRefreshInterval);

  useEffect(() => {
    if (user) { setAuthChecked(true); return; }
    fetch(`${API}/me`, { credentials: "include" })
      .then((res) => { if (res.ok) return res.json(); throw new Error(); })
      .then((data) => { setUser(data); setAuthChecked(true); })
      .catch(() => { setUser(null); setAuthChecked(true); });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || location.pathname !== "/") return;
    let redirected = false;
    try {
      const raw = localStorage.getItem(TEAMS_RELOGIN_RESUME_KEY);
      if (raw) {
        // Resume is single-use: consume once so it never hijacks regular menu navigation.
        localStorage.removeItem(TEAMS_RELOGIN_RESUME_KEY);
        const resume = JSON.parse(raw);
        const savedAt = Number(resume?.savedAt || 0);
        const isFresh = savedAt > 0 && (Date.now() - savedAt) <= TEAMS_RELOGIN_RESUME_MAX_AGE_MS;
        if (isFresh && resume?.route && resume.route !== "/") {
          navigate(resume.route, { replace: true });
          redirected = true;
        }
      }
    } catch {
      localStorage.removeItem(TEAMS_RELOGIN_RESUME_KEY);
    }
    if (!redirected) {
      const defaultRoute = isExternalUser(user) ? "/defectsv2" : "/masterv2";
      navigate(defaultRoute, { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const handleLogout = async () => {
    let logoutUrl = null;
    try {
      const r = await fetch(`${API}/logout`, { method: "POST", credentials: "include" });
      const data = await r.json().catch(() => ({}));
      logoutUrl = data?.logout_url || null;
    } catch { /* proceed even if call fails */ }
    clearCache();
    setUser(null);
    if (logoutUrl) {
      // Full-page navigation (not just discarding the response) so the
      // browser actually visits Microsoft's end-session endpoint and its
      // own AAD SSO session gets cleared too — otherwise clicking Login
      // again silently re-authenticates with no prompt.
      window.location.href = logoutUrl;
    } else {
      navigate("/", { replace: true });
    }
  };

  const isLoginPage = location.pathname === "/";
  const isExternal = isExternalUser(user);
  const internalOnlyAccess = authChecked ? !isExternal : null;
  const accessAllowed = !user || hasSiteAccess(user);

  useEffect(() => {
    if (!user || !isExternal) return;
    if (isLoginPage || PUBLIC_AUTH_PATHS.has(location.pathname)) return;
    if (EXTERNAL_ALLOWED_PATHS.has(location.pathname) || ROUTE_ALIASES[location.pathname]) return;
    navigate("/defectsv2", { replace: true });
  }, [user, isExternal, isLoginPage, location.pathname, navigate]);

  const widgetContextValue = {
    isOpen:            widgetOpen,
    launch:            launchWidget,
    close:             closeWidget,
    monRefreshInterval,
    onMonRefreshChange: handleMonRefreshChange,
  };

  return (
    <AlertWidgetContext.Provider value={widgetContextValue}>
    <PermissionsProvider>
    <div className="app">
      <div className="app-body">
        <main className="main-content">
          {user && !isLoginPage && !accessAllowed ? (
            <NoAccessPage onLogout={handleLogout} />
          ) : (
          <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route path="/email-signin" element={<LoginPage initialMode="email" />} />
              <Route path="/email-register" element={<EmailRegisterPage />} />
              <Route path="/email-reset-password" element={<EmailResetPasswordPage />} />
              {Object.entries(ROUTE_ALIASES).map(([from, to]) => (
                <Route key={from} path={from} element={<AliasRedirect to={to} />} />
              ))}
              <Route path="/dashboard" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><DashboardViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/epics" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={authChecked ? isAdminOrAmInternal(user) : null}><EpicsViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/masterv2" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><TasksViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/dependencies" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><DependenciesViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/features" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={internalOnlyAccess}><FeaturesViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={internalOnlyAccess}><ProjectsViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={authChecked ? isAdminInternal(user) : null}><UsersViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/timesheet" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={internalOnlyAccess}><TimesheetViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/meetingsv2" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><MeetingsViewV2 user={user} /></ProtectedRoute>} />
              <Route path="/defectsv2" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><DefectsViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={authChecked ? isAdminAmOrTeamLead(user) : null}><ReportsViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/todo" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><TodoView user={user} /></ProtectedRoute>} />
              <Route path="/releases" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><ProductReleasesView user={user} /></ProtectedRoute>} />
              <Route path="/retrov2new" element={<ProtectedRoute authStatus={authChecked ? !!user : null}><RetroViewV2Page user={user} /></ProtectedRoute>} />
              <Route path="/presales" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={authChecked ? isSuperAdminInternal(user) : null}><PresalesViewV2Page showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/copilot" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={authChecked ? isSuperAdminInternal(user) : null}><CopilotPage showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/access-control" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={authChecked ? isAdminInternal(user) : null}><RbacManagementPage showToast={showToast} user={user} /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute authStatus={authChecked ? !!user : null} roleCheck={authChecked ? isAdminInternal(user) : null}><SettingsPage user={user} /></ProtectedRoute>} />
          </Routes>
          )}
        </main>
      </div>

      {toast && (
        <div className={`toast toast--${toast.type}`}>{toast.msg}</div>
      )}
      {widgetPipWindow && createPortal(
        <AlertWidget firingCustomers={widgetFiringCustomers} lastUpdated={widgetLastUpdated} />,
        widgetPipWindow.document.body
      )}
    </div>
    </PermissionsProvider>
    </AlertWidgetContext.Provider>
  );
}
